// src/pages/DriversTodayPage.js
// Live "who's available / scheduled / on-a-move / off-today" view.
// Backed by GET /driver/roster — see backend/routes/driver_mgmt.js.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../scripts/api";
import {
  Truck, Calendar, CircleCheck, CircleOff, RefreshCw, Phone, Mail, MapPin,
} from "lucide-react";

const BUCKET_META = {
  on_move:         { label: "On a Move",      color: "#2563eb", bg: "#dbeafe", icon: Truck       },
  scheduled_today: { label: "Scheduled Today", color: "#7c3aed", bg: "#ede9fe", icon: Calendar    },
  available:       { label: "Available",       color: "#16a34a", bg: "#dcfce7", icon: CircleCheck },
  off_today:       { label: "Off Today",       color: "#dc2626", bg: "#fee2e2", icon: CircleOff   },
};

const BUCKET_ORDER = ["on_move", "scheduled_today", "available", "off_today"];

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

export default function DriversTodayPage() {
  const nav = useNavigate();
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState({ roster: [], buckets: {} });
  const [busy, setBusy] = useState(false);
  const [msg,  setMsg]  = useState("");

  const load = useCallback(async () => {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.get("/driver/roster", { params: { date } });
      setData(data || { roster: [], buckets: {} });
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to load roster.");
      setData({ roster: [], buckets: {} });
    } finally {
      setBusy(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s so the dashboard stays current on a TV/wallboard.
  useEffect(() => {
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  const grouped = useMemo(() => {
    const g = { on_move: [], scheduled_today: [], available: [], off_today: [] };
    for (const row of data.roster || []) {
      if (g[row.bucket]) g[row.bucket].push(row);
      else g.available.push(row);
    }
    return g;
  }, [data]);

  return (
    <div className="container">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900 }}>Drivers Today</h2>
          <div className="muted" style={{ marginTop: 4 }}>
            Live roster — updates every 30 seconds.
          </div>
        </div>
        <div className="row" style={{ gap: 8, alignItems: "center" }}>
          <input
            type="date"
            className="input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ maxWidth: 180 }}
          />
          <button className="btn-outline" onClick={load} disabled={busy} title="Refresh now">
            <RefreshCw size={16} style={{ marginRight: 6, verticalAlign: -2 }} />
            Refresh
          </button>
          <button className="btn" onClick={() => nav("/admin/moves")}>Drive-Away</button>
        </div>
      </div>

      {msg && <div className="card" style={{ marginTop: 10, color: "#dc2626" }}>{msg}</div>}

      {/* Summary pills */}
      <div className="row" style={{ gap: 8, marginTop: 14, flexWrap: "wrap" }}>
        {BUCKET_ORDER.map((key) => {
          const meta = BUCKET_META[key];
          const Icon = meta.icon;
          const count = data.buckets?.[key] || 0;
          return (
            <div
              key={key}
              className="card"
              style={{
                flex: "1 1 180px",
                minWidth: 160,
                background: meta.bg,
                color: meta.color,
                border: `1px solid ${meta.color}33`,
                padding: 14,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
                  <Icon size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
                  {meta.label}
                </div>
                <div style={{ fontSize: 28, fontWeight: 900 }}>{count}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Columns */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 12,
          marginTop: 14,
          alignItems: "flex-start",
        }}
      >
        {BUCKET_ORDER.map((key) => {
          const meta = BUCKET_META[key];
          const rows = grouped[key] || [];
          return (
            <div key={key} className="card" style={{ background: "var(--card-bg)" }}>
              <div
                style={{
                  borderLeft: `4px solid ${meta.color}`,
                  paddingLeft: 10,
                  marginBottom: 10,
                  fontWeight: 900,
                  fontSize: 15,
                  color: meta.color,
                }}
              >
                {meta.label} ({rows.length})
              </div>
              {rows.length === 0 && <div className="muted" style={{ fontSize: 13 }}>— none —</div>}
              <div style={{ display: "grid", gap: 8 }}>
                {rows.map((r) => (
                  <DriverCard key={r.driver.DriverId} row={r} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DriverCard({ row }) {
  const { driver, active_move, next_scheduled, unavailability } = row;
  return (
    <div
      className="card"
      style={{
        padding: 12,
        background: "rgba(255,255,255,0.02)",
        border: "1px solid var(--card-border)",
      }}
    >
      <div style={{ fontWeight: 800, fontSize: 15 }}>{driver.FullName}</div>

      <div className="muted" style={{ fontSize: 12, marginTop: 4, display: "grid", gap: 2 }}>
        {driver.home_terminal && (
          <div style={{ fontWeight: 700, letterSpacing: 0.2 }}>🏠 {driver.home_terminal}</div>
        )}
        {driver.current_location && (
          <div><MapPin size={11} style={{ verticalAlign: -1, marginRight: 4 }} />{driver.current_location}</div>
        )}
        {driver.Phone && (
          <div><Phone size={11} style={{ verticalAlign: -1, marginRight: 4 }} />{driver.Phone}</div>
        )}
        {driver.Email && (
          <div><Mail size={11} style={{ verticalAlign: -1, marginRight: 4 }} />{driver.Email}</div>
        )}
      </div>

      {active_move && (
        <div style={{ marginTop: 8, padding: 8, background: "#dbeafe", borderRadius: 6, color: "#1e40af" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
            On move #{active_move.TripId}
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>
            → {active_move.destination || "(no destination)"}
          </div>
          <div style={{ fontSize: 11, marginTop: 2 }}>
            {active_move.md_start}{active_move.md_end ? ` → ${active_move.md_end}` : ""}
            {active_move.move_type === "sna_to_customer" && " · to customer"}
          </div>
        </div>
      )}

      {!active_move && next_scheduled && (
        <div style={{ marginTop: 8, padding: 8, background: "#ede9fe", borderRadius: 6, color: "#5b21b6" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
            Next: move #{next_scheduled.TripId}
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>
            → {next_scheduled.destination || "(no destination)"}
          </div>
          <div style={{ fontSize: 11, marginTop: 2 }}>starts {next_scheduled.md_start}</div>
        </div>
      )}

      {unavailability && unavailability.length > 0 && (
        <div style={{ marginTop: 8, padding: 8, background: "#fee2e2", borderRadius: 6, color: "#b91c1c" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
            {unavailability[0].type === "holiday" ? "Holiday" : "Time off"}
          </div>
          {unavailability[0].reason && (
            <div style={{ fontSize: 12, marginTop: 2 }}>{unavailability[0].reason}</div>
          )}
          <div style={{ fontSize: 11, marginTop: 2 }}>
            {unavailability[0].start_date} → {unavailability[0].end_date}
          </div>
        </div>
      )}
    </div>
  );
}
